import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import ExpandedView from "./ExpandedView";
import CollapsedView from "./CollapsedView";
import { withGetConfig } from "../hooks/useGetConfig";

function debounce(fn, ms) {
  let timer;
  return () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn.apply(this, arguments);
    }, ms);
  };
}

const NotificationBanner = ({
  result = {
    content: {
      color: "blue",
      content: null,
      dismissable: true,
      enabled: true,
      label: "Update",
    },
  },
}) => {
  const {
    color = "blue",
    content,
    dismissable,
    enabled,
    label = "Update",
  } = result.content;

  const bannerContainerRef = useRef(null);
  const collapsedWrapperRef = useRef(null);
  const collapsedBannerRef = useRef(null);
  const VIEW_BREAKPOINT = 767;
  const [height, setHeight] = useState("100%");
  const [expanded, setExpanded] = useState();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const cssDeterminesBannerView = expanded ? "expanded" : "collapsed";

  useEffect(() => {
    //setBannerHeight on Load & Resize
    setBannerHeight();

    if (window.innerWidth <= VIEW_BREAKPOINT) {
      setExpanded(false);
    }

    const debounceBrowserResizeHandler = debounce(() => {
      return setWindowWidth(window.innerWidth);
    }, 600);

    window.addEventListener("resize", debounceBrowserResizeHandler);

    return () => {
      window.removeEventListener("resize", debounceBrowserResizeHandler);
    };
  }, [windowWidth]);

  useEffect(() => {
    //  setExpanded => updates cssDeterminesBannerView
    if (Object.keys(result).length > 0) {
      setExpanded(isNotificationExpanded());
    }
  }, [result]);

  const isNotificationExpanded = () => {
    const dateDismissed = new Date(result.content.dateDismissed);
    const dateUpdated = new Date(result.content.dateUpdated);
    const notDismissed =
      (isDateValid(dateUpdated) &&
        isDateValid(dateDismissed) &&
        dateUpdated >= dateDismissed) ||
      !isDateValid(dateDismissed);
    return notDismissed;
  };

  const setBannerHeight = () => {
    if (content) {
      bannerContainerRef.current.style.transition = "none";
      expanded
        ? collapsedBannerRef.current.setAttribute("tabIndex", "-1")
        : collapsedBannerRef.current.setAttribute("tabIndex", " ");

      setHeight(
        expanded ? "100%" : `${collapsedWrapperRef.current.offsetHeight}px`,
      );
    }
  };

  /**
   * Whether the date is valid, i.e. not null
   *
   * @param {Date} date
   * @return {Boolean}
   */

  const isDateValid = (date) => {
    return !isNaN(Date.parse(date));
  };
  /**
   * Try to find this NotificationBanner instance in localStorage, or else add
   * a minimal version of it, and mark it with a dateDismissed, then update
   * localStorage.
   */

  const markNotificationClosed = () => {
    const dateDismissed = new Date().toISOString();
    const storageNotification = result || {};

    if (storageNotification && Object.keys(storageNotification).length > 0) {
      window.localStorage.setItem(
        "notifications",
        JSON.stringify({
          ...storageNotification,
          content: {
            ...storageNotification.content,
            dateDismissed,
          },
        }),
      );
    } else {
      window.localStorage.setItem(
        "notifications",
        JSON.stringify({
          ...storageNotification,
          content: {
            name: "header-notification",
            dateDismissed,
          },
        }),
      );
    }
  };

  /**
   * Callback when the user dimisses a notification. Mark it as dismissed in
   * localStorage, empty the contents, and dispatch a custom event to notify
   * any observers that it has been dismissed.
   */

  const collapseNotification = (e) => {
    e.stopPropagation();
    markNotificationClosed();
    collapsedBannerRef.current.setAttribute("tabIndex", " ");
    bannerContainerRef.current.style.transition = "height .2s ease-out";

    setExpanded(false);
    setHeight(`${collapsedWrapperRef.current.offsetHeight}px`);
  };

  const expandNotification = () => {
    collapsedBannerRef.current.setAttribute("tabIndex", "-1");
    bannerContainerRef.current.style.transition = "height .2s ease-out";
    setExpanded(true);
    setHeight("100%");
  };

  /**
   * Render the notification if it has any contents.
   *
   * @return {String} HTML content
   */

  return (
    <>
      {!content || !enabled ? (
        <div id="notification-banner" />
      ) : (
        <div
          ref={bannerContainerRef}
          style={{ height }}
          className={`notification-banner notification-banner-${color} ${cssDeterminesBannerView}`}
        >
          <ExpandedView
            label={label}
            expanded={expanded}
            content={content}
            dismissable={dismissable}
            collapseNotification={collapseNotification}
          />

          <CollapsedView
            expandNotification={expandNotification}
            label={label}
            ref={{ collapsedWrapperRef, collapsedBannerRef }}
          />
        </div>
      )}
    </>
  );
};

NotificationBanner.propTypes = {
  result: PropTypes.shape({
    content: PropTypes.shape({
      color: PropTypes.string,
      content: PropTypes.string,
      dateDismissed: PropTypes.string,
      dateUpdated: PropTypes.string,
      dismissable: PropTypes.bool,
      enabled: PropTypes.bool,
      label: PropTypes.string,
    }),
  }),
};

export default withGetConfig(NotificationBanner, {
  timeout: 5,
  url: "/config/notification",
  localStorageName: "active_notification",
  equalityCheckExclude: ["dateDismissed", "dateUpdated"],
  defaultContent: {},
});
